import { useForm } from 'react-hook-form';

import { AlertCircle } from 'lucide-react';
import { z } from 'zod';

import { showPromiseToast } from '@/lib/toast';

import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@/components/ui/form';
import { Input } from '@/components/ui/input';

import { useUnsubscribeFromNotificationsMutation } from '@/store/api/serviceHealthNotificationSubscriptionApi';

import { zodResolver } from '@hookform/resolvers/zod';

const unsubscribeSchema = z.object({
  email: z.string().email('Please enter a valid email address'),
});

type UnsubscribeFormData = z.infer<typeof unsubscribeSchema>;

interface UnsubscribeModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  clientShortName: string;
}

const UnsubscribeModal = ({ open, onOpenChange, clientShortName }: UnsubscribeModalProps) => {
  const [unsubscribeFromNotifications] = useUnsubscribeFromNotificationsMutation();

  const form = useForm<UnsubscribeFormData>({
    resolver: zodResolver(unsubscribeSchema),
    defaultValues: {
      email: '',
    },
  });

  const onSubmit = async (data: UnsubscribeFormData) => {
    await showPromiseToast(
      unsubscribeFromNotifications({
        email: data.email,
        client_short_name: clientShortName,
      }).unwrap(),
      {
        loading: 'Unsubscribing from status updates...',
        success: `Successfully unsubscribed ${data.email} from status updates`,
        error: (error: any) =>
          error?.data?.detail || error?.message || 'Failed to unsubscribe from notifications',
      },
    );

    // Reset form and close modal
    form.reset();
    onOpenChange(false);
  };

  const handleCancel = () => {
    form.reset();
    onOpenChange(false);
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className='sm:max-w-md'>
        <DialogHeader>
          <DialogTitle className='flex items-center gap-2 mb-2'>
            <AlertCircle className='h-5 w-5 text-orange-600' />
            Unsubscribe from status updates
          </DialogTitle>
          <DialogDescription>
            Enter your email address to unsubscribe from status notifications. You will no longer
            receive email alerts about service updates for this client.
          </DialogDescription>
        </DialogHeader>

        <Form {...form}>
          <form onSubmit={form.handleSubmit(onSubmit)} className='space-y-4 py-4'>
            <FormField
              control={form.control}
              name='email'
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Email Address</FormLabel>
                  <FormControl>
                    <Input placeholder='your.email@example.com' {...field} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <div className='flex gap-2 justify-end'>
              <Button
                type='button'
                variant='outline'
                onClick={handleCancel}
                disabled={form.formState.isSubmitting}
              >
                Cancel
              </Button>
              <Button type='submit' variant='destructive' disabled={form.formState.isSubmitting}>
                {form.formState.isSubmitting ? 'Unsubscribing...' : 'Unsubscribe'}
              </Button>
            </div>
          </form>
        </Form>
      </DialogContent>
    </Dialog>
  );
};

export default UnsubscribeModal;
